// Spec: specs/stats/questions/functional_requirements.md §4.16
//
// Why a question's chart slot is empty, and what — if anything — the reader can do about it. The
// job's status describes the calculation; this describes the report, which is what is actually
// being asked. A question below its threshold and a question whose analysis failed share a status
// and share nothing else.

import { JOB_STATUS } from '@aytm/app/stats/page/question-views/components/data-run/lib/utils'

export const CAUSE = {
  belowThreshold: 'belowThreshold',
  notCalculated: 'notCalculated',
  notYetCalculated: 'notYetCalculated',
  calculating: 'calculating',
  couldNotCalculate: 'couldNotCalculate',
  stillFilling: 'stillFilling',
  notSetUp: 'notSetUp'
}

// What the reader can do about it. A state never offers an action that cannot change its own cause.
export const REMEDY = { none: 'none', wait: 'wait', run: 'run', edit: 'edit' }

const REMEDY_FOR = {
  [CAUSE.belowThreshold]: REMEDY.wait,
  [CAUSE.notCalculated]: REMEDY.none,
  [CAUSE.notYetCalculated]: REMEDY.run,
  [CAUSE.calculating]: REMEDY.none,
  [CAUSE.couldNotCalculate]: REMEDY.none,
  [CAUSE.stillFilling]: REMEDY.wait,
  [CAUSE.notSetUp]: REMEDY.edit
}

// The two types that declare a minimum sample. Everything else has none, and must never imply that
// collecting more responses is the remedy — for the rest, bad data means the analysis could not
// resolve, which more responses may not fix.
export const THRESHOLDS = { md: 150, cjd: 400 }

// A type whose analysis is cheap and assembles as responses arrive has nothing to offer a button
// for: it is not waiting to be run, it is waiting to be filled.
export const INCREMENTAL = new Set(['hm'])

export const thresholdFor = type => THRESHOLDS[type] ?? null

export const resolveCause = ({
  type,
  jobStatus,
  isRunning = false,
  setUp = true,
  failed = false,
  isGuest = false,
  runnable = true
} = {}) => {
  if (isRunning || JOB_STATUS.running === jobStatus) return CAUSE.calculating
  if (!setUp) return CAUSE.notSetUp

  // never notYetCalculated: that remedy is a run, and a type that fills itself has no job to offer
  if (INCREMENTAL.has(type)) return CAUSE.stillFilling

  if (JOB_STATUS.disabled === jobStatus) {
    // A guest's idle job is rewritten to disabled upstream, because a guest cannot start a run. So
    // for a guest this status no longer says anything about the data, and reading a shortfall out
    // of it would tell them a report needs responses it may already have.
    if (isGuest) return CAUSE.notCalculated

    // Only a type with a declared minimum can be short of one. Both minima are the ones the old
    // per-block notes stated; neither is derived here.
    if (null != thresholdFor(type)) return CAUSE.belowThreshold

    // For the rest, "the job cannot start" does not say why. It covers an analysis that ran and
    // failed and one that was never run at all -- and a guest's idle job is demoted to this, so
    // treating it as failure tells a guest their report is broken when it is merely uncalculated.
    // The service leaves a message behind only when something actually went wrong; that is the
    // difference, and without it the honest state is the neutral one.
    return failed ? CAUSE.couldNotCalculate : CAUSE.notCalculated
  }

  // A guest can start nothing, so the state that exists to offer a run is never theirs to see --
  // whatever status reaches here. Upstream demotes their idle job already; this does not rely on it.
  // Nor is it the state of a block that has no run to start: only a type with a job of its own
  // (MaxDiff, Conjoint Segmentation) ever offered one.
  return !runnable || isGuest ? CAUSE.notCalculated : CAUSE.notYetCalculated
}

export const remedyFor = cause => REMEDY_FOR[cause] ?? REMEDY.none

// A state nothing is going to move on its own: the analysis was attempted and did not resolve, or
// the question is missing something only the editor can supply. Waiting changes neither.
export const TERMINAL = new Set([CAUSE.couldNotCalculate, CAUSE.notSetUp])

// The mark is a promise that something is coming -- responses, or a result. Where nothing is
// actually in flight that promise is false, so a terminal state carries no mark at all and says
// its piece in words. `population` is a fraction of the field: sparse where there is little yet.
export const markFor = cause => {
  if (TERMINAL.has(cause)) return null
  if (CAUSE.calculating === cause) return { population: 1, still: false }
  if (CAUSE.notCalculated === cause) return { population: 0.45, still: true }
  if (CAUSE.notYetCalculated === cause) return { population: 1, still: true }

  return { population: 0.45, still: true }
}
